import i18n from 'i18next';
import { ListColumnOptionType, ListColumnPreferenceEntry, ListColumnType } from 'types/ListTypes';
import { buildGenericColumnRender } from './renderGenericColumn';

/*
 * columnPreferences.ts
 * Helpers for the user chosen list columns. They resolve the label
 * of a column option, merge a stored column preference with the
 * hand written column definitions of a list, estimate the width the
 * chosen columns need and pick the smallest list layout they fit in.
 */

// Default width of an extra column the user adds to a list
export const DEFAULT_EXTRA_COLUMN_WIDTH = '150px';

// Estimated minimum pixel width of a flexible column, the fr value
// itself is a ratio and says nothing about the width a column needs
const ESTIMATED_FLEXIBLE_COLUMN_WIDTH = 120;

// The list layouts from narrow to full screen, every layout is a container class
export type ListWidthTier = 'container-filled' | 'container-wide' | 'container-wide-filled';
export const LIST_WIDTH_TIERS: ListWidthTier[] = ['container-filled', 'container-wide', 'container-wide-filled'];

// Horizontal padding of a container, 0.9375rem on both sides
const CONTAINER_PADDING = 30;

// Pixel widths of the pseudo columns the list adds around the chosen columns
export const SELECT_COLUMN_WIDTH = 30;
export const DRAG_COLUMN_WIDTH = 22;
export const DELETE_COLUMN_WIDTH = 20;

// Share of its normal width a flexible column may shrink to before the list widens
const FLEXIBLE_COLUMN_SHRINK_RATIO = 0.7;

// Minimum pixel width a flexible column keeps when converted to pixels
const MIN_CONVERTED_COLUMN_WIDTH = 60;

// Resolves the label of a column option, tries the translation key first
export const getColumnOptionLabel = (listKey: string, option: ListColumnOptionType): string => {

    // A custom field label is user text and never a translation key
    if (option.type === 'custom_field') {
        return option.label;
    }
    const translationKey = `${listKey}.fields.${option.name}`;
    return i18n.exists(translationKey) ? translationKey : option.label;
};

// Merges a stored column preference with the default column definitions
export const mergeColumnsWithPreference = ({
    preferenceColumns,
    defaultColumns,
    columnOptions,
    listKey,
    userLocale,
    timeFormat = '24h',
}: {
    preferenceColumns: ListColumnPreferenceEntry[];
    defaultColumns: ListColumnType[];
    columnOptions: ListColumnOptionType[];
    listKey: string;
    userLocale: string;
    timeFormat?: '12h' | '24h';
}): ListColumnType[] => {

    // Index the backend column options by their field name
    const optionsByName = new Map(columnOptions.map(option => [option.name, option]));

    const mergedColumns: ListColumnType[] = [];
    preferenceColumns.forEach(entry => {

        // A field matching a hand written column keeps its renderer and label
        const defaultColumn = defaultColumns.find(column => column.field === entry.field);
        if (defaultColumn) {
            mergedColumns.push({ ...defaultColumn, width: entry.width ?? defaultColumn.width });
            return;
        }

        // Other fields become a generic column built from the backend option
        const option = optionsByName.get(entry.field);
        if (!option) {
            return;
        }
        mergedColumns.push({
            width: entry.width ?? DEFAULT_EXTRA_COLUMN_WIDTH,
            field: option.name,
            label: getColumnOptionLabel(listKey, option),
            rawLabel: option.type === 'custom_field',
            orderField: option.order_field || undefined,
            disableOrdering: !option.order_field,
            render: buildGenericColumnRender(option, userLocale, listKey, timeFormat),
        });
    });

    return mergedColumns;
};

// Sums the pixel widths of the pseudo columns a list renders around its columns
export const getPseudoColumnsWidth = ({ hasSelectColumn, isDraggable, hasDeleteColumn }: {
    hasSelectColumn: boolean;
    isDraggable: boolean;
    hasDeleteColumn: boolean;
}): number => (
    (hasSelectColumn ? SELECT_COLUMN_WIDTH : 0)
    + (isDraggable ? DRAG_COLUMN_WIDTH : 0)
    + (hasDeleteColumn ? DELETE_COLUMN_WIDTH : 0)
);

// Returns the pixel width a list layout offers its table at the given
// viewport width, following the container breakpoints of the stylesheet
export const getListTierContentWidth = (tier: ListWidthTier, viewportWidth: number): number => {
    let maxWidth = viewportWidth;

    // The narrow container grows in two steps
    if (tier === 'container-filled') {
        maxWidth = viewportWidth >= 1610 ? 1248 : viewportWidth >= 992 ? 1060 : viewportWidth;
    }

    // The extended container grows in two larger steps
    if (tier === 'container-wide') {
        maxWidth = viewportWidth >= 1610 ? 1600 : viewportWidth >= 1366 ? 1366 : viewportWidth;
    }

    // The full width layout drops the container padding and runs edge to edge
    if (tier === 'container-wide-filled') {
        return viewportWidth;
    }
    return Math.min(maxWidth, viewportWidth) - CONTAINER_PADDING;
};

// Estimates the total pixel width the chosen columns need to render
export const estimateColumnsWidth = (columns: ListColumnType[], pseudoColumnsWidth: number): number => {
    return columns.reduce((totalWidth, column) => {
        const width = column.width ?? '';

        // Count pixel widths as they are
        if (width.endsWith('px')) {
            return totalWidth + (parseFloat(width) || 0);
        }

        // Count every flexible column as its estimated minimum width
        return totalWidth + ESTIMATED_FLEXIBLE_COLUMN_WIDTH;
    }, pseudoColumnsWidth);
};

// Estimates the width the chosen columns need before the list has to widen,
// pixel columns count fully while flexible columns may shrink a bit below
// the width they take in the default layout of the list
export const estimateRequiredTableWidth = (
    columns: ListColumnType[],
    defaultColumns: ListColumnType[],
    pseudoColumnsWidth: number,
    defaultContentWidth: number,
): number => {

    // Resolve the width every flexible column takes in the default layout
    const pixelColumns = convertColumnsToPixelWidths(columns, defaultColumns, defaultContentWidth - pseudoColumnsWidth);

    return columns.reduce((totalWidth, column, index) => {
        const width = column.width ?? '';

        // A flexible column may shrink to a share of its default width
        if (width.endsWith('fr')) {
            const defaultWidth = parseFloat(pixelColumns[index].width) || ESTIMATED_FLEXIBLE_COLUMN_WIDTH;
            return totalWidth + Math.max(MIN_CONVERTED_COLUMN_WIDTH, Math.round(defaultWidth * FLEXIBLE_COLUMN_SHRINK_RATIO));
        }

        // Count the pixel width of the column, falls back to the extra column default
        return totalWidth + (parseFloat(width) || parseFloat(DEFAULT_EXTRA_COLUMN_WIDTH));
    }, pseudoColumnsWidth);
};

// Picks the smallest list layout the required width fits in, starting at
// the base layout of the list and ending at the full width layout
export const resolveListWidthTier = (requiredWidth: number, viewportWidth: number, baseTier: ListWidthTier): ListWidthTier => {
    const candidateTiers = LIST_WIDTH_TIERS.slice(LIST_WIDTH_TIERS.indexOf(baseTier));

    // Return the first layout which offers enough width
    const fittingTier = candidateTiers.find(tier => requiredWidth <= getListTierContentWidth(tier, viewportWidth));
    return fittingTier ?? 'container-wide-filled';
};

// Converts the flexible columns to the fixed pixel width they take in the
// default layout of the list, the available width is the content width
// of the container minus the pseudo columns
export const convertColumnsToPixelWidths = (columns: ListColumnType[], defaultColumns: ListColumnType[], availableContentWidth: number): ListColumnType[] => {
    const defaultFields = new Set(defaultColumns.map(column => column.field));

    // Sum the pixel widths of the default columns and the total flexible ratio
    let defaultPixelSum = 0;
    let flexibleRatioTotal = 0;
    columns.forEach(column => {
        const width = column.width ?? '';
        if (width.endsWith('fr')) {
            flexibleRatioTotal += parseFloat(width) || 1;
        } else if (defaultFields.has(column.field)) {
            defaultPixelSum += parseFloat(width) || 0;
        }
    });

    // The flexible columns share the space the default layout leaves over
    const availableWidth = Math.max(0, availableContentWidth - defaultPixelSum);

    // Convert every flexible column to its proportional pixel width
    return columns.map(column => {
        const width = column.width ?? '';
        if (!width.endsWith('fr') || !flexibleRatioTotal) {
            return column;
        }
        const ratio = parseFloat(width) || 1;
        const pixelWidth = Math.max(MIN_CONVERTED_COLUMN_WIDTH, Math.round((ratio / flexibleRatioTotal) * availableWidth));
        return { ...column, width: `${pixelWidth}px` };
    });
};
